import React, { useState, useEffect } from 'react';
import Badge from '../../components/common/Badge';
import Loader from '../../components/common/Loader';
import { apiClient } from '../../api/client';
import { useToast } from '../../context/ToastContext';

const AdminLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { addToast } = useToast();

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/logs');
      setLogs(res.data || []);
    } catch (err) {
      addToast('Failed to load audit logs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filtered = logs.filter(l => {
    const searchString = `${l.actor_email} ${l.action} ${l.resource_type} ${l.details}`.toLowerCase();
    return searchString.includes(searchTerm.toLowerCase());
  });

  const getActionColor = (action) => {
    const a = action.toLowerCase();
    if (a.includes('create') || a.includes('approve') || a.includes('register')) return 'success';
    if (a.includes('delete') || a.includes('reject') || a.includes('block') || a.includes('suspend')) return 'danger';
    if (a.includes('update') || a.includes('edit')) return 'primary';
    return 'gray';
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="admin-page-title">Audit Logs</h1>
          <p className="admin-page-sub">System activity and security events.</p>
        </div>
      </div>

      <div className="card-elevated">
        <div className="flex justify-between items-center mb-6">
          <div className="search-bar w-full max-w-md">
            <span className="search-icon text-sm">🔍</span>
            <input 
              type="text" 
              placeholder="Search by user, action, or details..." 
              className="text-sm py-2"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No logs found matching your criteria.</div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(log => (
                  <tr key={log.id}>
                    <td className="text-xs text-gray-500 whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td>
                      <div className="text-sm font-medium text-gray-900">{log.actor_email}</div>
                      <Badge variant="gray" className="!text-[10px] !py-0.5 !px-1.5 mt-1 capitalize">{log.actor_type}</Badge>
                    </td>
                    <td>
                      <Badge variant={getActionColor(log.action)} className="uppercase text-[10px]">
                        {log.action}
                      </Badge>
                    </td>
                    <td>
                      <div className="text-sm font-semi text-gray-700 capitalize">{log.resource_type}</div>
                      <div className="text-xs text-gray-400">ID: {log.resource_id}</div>
                    </td>
                    <td className="text-sm text-gray-600 max-w-xs truncate" title={log.details}>
                      {log.details || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLogs;
